/**
 * Public Homepage - Deal Finder (Layout-Driven)
 * 
 * CR204: This page now renders ONLY what the published Layout Studio preset defines.
 * The LayoutRenderer is the SINGLE SOURCE OF TRUTH for page composition.
 * 
 * CR227: Added AdminConfigBadge to verify the public site is using the
 * published Layout Studio snapshot (not hardcoded values).
 * 
 * CR228: Removed hardcoded AmbiguousAccordion - now a Layout block.
 *        All UI (filters, empty state, ambiguous deals) is layout-driven.
 * 
 * Structure:
 * 1. AdminChrome - Auth bar + admin links (separate from layout content)
 * 2. AdminConfigBadge - Debug badge showing active config (admin only)
 * 3. LayoutRenderer - Renders all enabled blocks from published layout
 * 4. SiteDebugFooter - Debug info (only shows with ?debug=1)
 * 
 * IMPORTANT: No legacy hardcoded UI (search bars, filters, headings) should exist
 * outside of the LayoutRenderer. All page content is driven by Layout Studio blocks.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, User, Database, Rocket, Settings, Eye } from "lucide-react";
import { AdminLoginDialog } from "@/components/auth/AdminLoginDialog";
import { useAuth } from "@/contexts/AuthContext";
import { type DealCategory } from "@/lib/types/deals";
import type { SortOptionId } from "@/lib/layout-studio/types";
import { usePublicDeals } from "@/hooks/usePublicDeals";
import { usePublicLayout } from "@/hooks/usePublicLayout";
import { SiteDebugFooter } from "@/components/debug/SiteDebugFooter";
import { LayoutRenderer } from "@/components/layout/LayoutRenderer";
import { AdminConfigBadge } from "@/components/debug/AdminConfigBadge";
import type { BlockRenderResult } from "@/components/layout/blocks";

export default function Index() {
  const { user, isAdmin, signOut } = useAuth();
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  // CR204: Load published layout configuration
  const { 
    layout, 
    dealCardUX, 
    loading: layoutLoading, 
    debugInfo: layoutDebugInfo 
  } = usePublicLayout();

  // Public deals hook - single source of truth for all users
  const publicDeals = usePublicDeals();

  // Search and filter state - controlled by this page, passed to LayoutRenderer
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedDealTypes, setSelectedDealTypes] = useState<DealCategory[] | "All">("All");
  
  // CR320: Sort state - defaults to layout.defaultSort
  const [selectedSort, setSelectedSort] = useState<SortOptionId>(layout.defaultSort || "newest");
  
  // CR204: Track rendered blocks for debugging
  const [renderedBlocks, setRenderedBlocks] = useState<BlockRenderResult[]>([]);
  const handleBlocksRendered = useCallback((results: BlockRenderResult[]) => {
    setRenderedBlocks(results);
  }, []);

  // CR228: Process deals for debug badge counts
  const { ambiguousCount, confirmedCount } = useMemo(() => {
    const ambiguous = publicDeals.deals.filter((d) => d.confidence === "Low").length;
    const confirmed = publicDeals.deals.filter((d) => d.confidence !== "Low").length;
    return { ambiguousCount: ambiguous, confirmedCount: confirmed };
  }, [publicDeals.deals]);

  // Fetch deals on mount
  useEffect(() => {
    publicDeals.refetch();
  }, []);

  const handleLoginSuccess = () => {
    setShowLoginDialog(false);
    toast.success("Signed in successfully!");
  };

  const handleSignOut = async () => {
    await signOut();
    toast.info("Signed out");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Admin Login Dialog */}
      <AdminLoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        onSuccess={handleLoginSuccess}
      />
      
      {/* ========================================
          ADMIN CHROME - Separate from layout content
          ======================================== */}
      
      {/* Auth Bar */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex justify-end items-center">
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {user.email}
                  {isAdmin && (
                    <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs">
                      Admin
                    </span>
                  )}
                </span>
                <Button variant="ghost" size="sm" onClick={handleSignOut}>
                  <LogOut className="w-3 h-3 mr-1" />
                  Sign Out
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setShowLoginDialog(true)}>
                <User className="w-3 h-3 mr-1" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        
        {/* Admin Quick Links Banner - ADMIN CHROME (not layout content) */}
        {isAdmin && (
          <div className="p-4 border border-blue-500/20 bg-blue-500/5 rounded-lg">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-medium text-foreground">You're viewing Public</h3>
                  <p className="text-xs text-muted-foreground">
                    This shows the same data as end users. Use Live Preview for admin tools.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Link to="/admin/live-preview">
                  <Button variant="default" size="sm">
                    <Database className="w-4 h-4 mr-2" />
                    Live Preview
                  </Button>
                </Link>
                <Link to="/admin/batch-runs">
                  <Button variant="outline" size="sm">
                    <Rocket className="w-4 h-4 mr-2" />
                    Batch Runs & Publishing
                  </Button>
                </Link>
                <Link to="/admin/command-center">
                  <Button variant="outline" size="sm">
                    <Settings className="w-4 h-4 mr-2" />
                    Command Center
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* ========================================
            CR227/228: ADMIN CONFIG DEBUG BADGE
            Shows published config for verification.
            Only visible to admins or with ?admin=1
            ======================================== */}
        <AdminConfigBadge 
          activePresetId={layoutDebugInfo?.activePresetId}
          presetName={layoutDebugInfo?.presetName}
          renderedBlocksCount={renderedBlocks.filter(b => b.rendered).length}
          renderedBlockIds={renderedBlocks.filter(b => b.rendered).map(b => b.blockId)}
          dealsCount={confirmedCount}
          ambiguousDealsCount={ambiguousCount}
          className="mb-4"
        />

        {/* ========================================
            LAYOUT RENDERER - SINGLE SOURCE OF TRUTH
            All page content below AdminChrome is driven
            by the published Layout Studio preset.
            
            CR228: This now includes:
            - DealTypeFilterBlock (if enabled)
            - EmptyStateBlock (if enabled and no deals)
            - ResultsBlock (deal grid)
            - AmbiguousDealsBlock (if enabled)
            ======================================== */}
        <LayoutRenderer
          layout={layout}
          dealCardUX={dealCardUX}
          layoutLoading={layoutLoading}
          deals={publicDeals.deals}
          dealsLoading={publicDeals.loading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          selectedDealTypes={selectedDealTypes}
          onDealTypesChange={setSelectedDealTypes}
          selectedSort={selectedSort}
          onSortChange={setSelectedSort}
          isAdmin={false} // Always render as public - admin preview is on /admin/live-preview
          onBlocksRendered={handleBlocksRendered}
        />

        {/* CR228: Removed hardcoded AmbiguousAccordion - now rendered by AmbiguousDealsBlock */}
      </div>

      {/* Debug Footer - only shows in dev or with ?debug=1 */}
      <SiteDebugFooter 
        layoutDebugInfo={layoutDebugInfo} 
        renderedBlocks={renderedBlocks}
        deals={publicDeals.deals}
      />
    </div>
  );
}
